import React from 'react';
import { useTranslation } from 'react-i18next';
//import { useNavigate } from 'react-router-dom';
import { Skeleton } from "@heroui/react";

import { RoleContext } from '@/app/api/common';
import { store, useAppSelector } from '@/app/store';
import { hotelCheckoutOff, hotelCheckoutOn } from '@/features/master/masterSlice';
import { ChannelList, checkNoError, copyUrls, FieldDescriptor, fieldGreyedByCheckoutOff, fieldHiddenByCheckoutOn, getField, getFieldExpiry, getFieldActivation, getUrlFields, getWelcomeFields, screentimeAclKey, setField, setFieldExpiry, setFieldActivation, statisticsAclKey, UrlInfo } from '@/features/channels/channelsSlice';
import { canOverrideScreentime, hasLegacyScreentimeAcl } from '@/features/screentime/screenTimeApi';
import { LoginDescriptor } from '@/features/logins/loginsSlice';
import { copyProjectInfo, copyProjectLimits, copyProjectRoom, HotelInfo, HotelLimits, HotelRoom } from '@/features/projects/projectsSlice';

import Button from '@/components/Common/Button';
import CheckBox from '@/components/Common/CheckBox';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
import { WelField } from '@/components/Common/Field';
import FlexRow from '@/components/Common/FlexRow';
import Input, { TextArea } from '@/components/Common/Input'
import { InlineSchedulePicker } from '@/components/Common/UrlsEditor';

import { CatHeader } from './Project';

export interface WelcomeWizardProps {
  project:				HotelInfo;
  setProject:			(val: HotelInfo) => any;
  limits:				HotelLimits | null;
  setLimits:			(lim: HotelLimits) => any;
  room?:				HotelRoom;
  setRoom?:				(room: HotelRoom) => any;
  acls?:				boolean;
}

const WelcomeWizard = (props: WelcomeWizardProps) => {
  const { t } = useTranslation();
  //const navigate = useNavigate();
  
  const [fields] = React.useState(getWelcomeFields());
  const [urlFields] = React.useState(getUrlFields());

  const role = React.useContext(RoleContext);

  // §3.2 — the Netflix box has no effect while the Checkout is switched off
  // (no keep list goes out), so it is greyed with a hint then. It is admin-only
  // anyway, and Project.tsx loads /master.json for admins. Read here, above the
  // is_new early return, to keep the hook order fixed.
  const master = useAppSelector((state) => state.master);
  // #91 §0.8 — both follow the HOTEL's state: its own value when it has one,
  // else System settings. undefined = the hotel follows System settings.
  const own = props.limits ? props.limits.checkout_enabled : undefined;
  const checkoutOff = hotelCheckoutOff(own, master);
  // Long Stay is hidden while the Checkout is on for this hotel (#91).
  const checkoutOn = hotelCheckoutOn(own, master);

  React.useEffect(() => {
    if (props.project && props.project.id && !props.project.is_new) {
      store.getChannelLists(props.project.id, false);
    }
  }, [props.project?.id]);

  const updateWelcomeEnabled = (val: boolean) => {
    if (!props.limits)
      return;
    
    let limits = copyProjectLimits(props.limits);
    limits.welcome.enabled = val;
    
    props.setLimits(limits);
  };
  
  const acls =	props.acls
  				?	true
  				:	false;
  
  if (props.project.is_new)
    return null;
  
  const caption = 	acls
  					?	"projects:project:manager_acls:caption"
  					:	(	props.room
  							?	"projects:project:rooms:room_settings:caption"
  							:	"projects:project:welcome_wizard:caption"
  						);
  
  const welcomeEnabled =	props.limits &&
  							props.limits.welcome &&
  							props.limits.welcome.enabled
  							?	true
  							:	false;
  
  const loading =	!props.project.is_new &&
			  		props.project.id != '' &&
			  		!props.limits;
  
  const limitsUrls =	props.limits &&
  						props.limits.urls
  						?	props.limits.urls
  						:	[];
  
  const aclsUrls =	props.limits &&
					props.limits.acls
					?	props.limits.acls
					:	[];
  
  const limitsWelcomeFields =	props.limits &&
		  						props.limits.welcome.fields
								?	props.limits.welcome.fields
								:	[];

  const roomProjWelcomeFields =	React.useMemo(() => {
    const welcomeEnabled =	props.limits?.welcome?.enabled;
    const fields =	props.limits?.welcome?.fields
					?	props.limits.welcome.fields.filter((f: UrlInfo) => f.type !== "enable-welcome")
					:	[];

    if (welcomeEnabled)
      return [...fields, { type: "enable-welcome", url: "1", tags: [] }];

    return fields;
  }, [props.limits]);
  
  const roomFields =	props.room &&
						props.room.fields
						?	props.room.fields
						:	[];
  
  const updateRoomFields = (fields: UrlInfo[]) => {
	if (!props.room)
	  return;
    
	let room = copyProjectRoom(props.room);
	room.fields = fields;

	if (props.setRoom)
	  props.setRoom(room);
  };
  
  const updateLimitsWelcomeFields = (fields: UrlInfo[]) => {
    if (!props.limits)
	  return;
    
    let limits = copyProjectLimits(props.limits);
      
    limits.welcome.fields = fields;
    props.setLimits(limits);
  };
  
  const updateLimitsAcls  = (fields: UrlInfo[]) => {
    if (!props.limits)
	  return;
    
    let limits = copyProjectLimits(props.limits);
      
    limits.acls = fields;
    props.setLimits(limits);
  };
  
  const updateLimitsUrls  = (fields: UrlInfo[]) => {
    if (!props.limits)
	  return;
    
    let limits = copyProjectLimits(props.limits);
      
    limits.urls = fields;
    props.setLimits(limits);
  };
  
  const hasSeeAll = role?.privilages?.includes("SeeAllProjects");
  const logins = useAppSelector((state) => state.logins);
  const isProjectMember = React.useMemo(() => {
    const username = logins.login?.username;
    if (!username || !props.project) return false;
    const localProjectId = logins.login?.local_project_id;
    if (localProjectId && localProjectId === props.project.id) return true;

    if (props.project.owner === username) return true;
    if (!props.limits) return false;
    const execs = props.limits.executives;
    return execs?.partner?.includes(username) ||
           execs?.chain_manager?.includes(username) ||
           execs?.manager?.includes(username);
  }, [logins.login, props.project, props.limits]);
  const canEdit = role.admin
    || role?.privilages?.includes("EditProjectSettings")
    || (acls && role?.privilages?.includes("SetManagerACLs"))
    || (!acls && (role?.privilages?.includes("SetPartner") || role?.privilages?.includes("SetChainmanager") || role?.privilages?.includes("SetManager")))
    || (!acls && isProjectMember);
  const seeAllOnly = !canEdit;

  const hasWelcomeAcl = React.useMemo(() => {
    return	(	role.admin ||
				hasSeeAll ||
				getField(aclsUrls, "enable-welcome") == "1"
			);
  }, [fields, acls, role.admin, hasSeeAll, aclsUrls]);
  
  return (
  <FlexRow direction="column"
           align="start"
           className="py-0 gap-[16px]">
  {!acls && !props.room && hasWelcomeAcl &&
      <WelField label={t("projects:project:project_settings:welcome_enable")}>
        <CheckBox toggled={welcomeEnabled}
                  isLoaded={!loading}
                  setToggled={seeAllOnly ? () => {} : updateWelcomeEnabled} />
      </WelField>
  }
    <React.Fragment>
    {urlFields.map((field: FieldDescriptor, i: number) =>
      <WelcomeField key={i}
		            type="projects:project:urls"
		            fields={	acls
		            			?	aclsUrls
		            			:	(	props.room
		            					?	roomFields
		            					:	limitsUrls
		            				)
		            	   }
		            setFields={	acls
		            			?	updateLimitsAcls
		            			:	(	props.room
		            					?	updateRoomFields
		            					:	updateLimitsUrls
		            				)
		            		  }
		            projFields={limitsUrls}
		            limits={props.limits}
		            room={props.room}
		            isLoaded={!loading}
		            acl={acls}
		            isReadOnly={seeAllOnly}
		            isProjectMember={isProjectMember}
		            field={field} />
	)}
    </React.Fragment>
    <CatHeader caption={caption} />
  {fields.map((field: FieldDescriptor, i: number) =>
    (	!acls ||
    	field.acl
    ) &&
    (	!field.roles ||
    	field.roles.length == 0 ||
    	field.roles.indexOf(role.role) > -1
    ) &&
    !fieldHiddenByCheckoutOn(field, checkoutOn)
    ?   <WelcomeField key={i}
		              type="welcome:fields"
		              fields={	acls
		            			?	aclsUrls
		            			:	(	props.room
		            					?	roomFields
		            					:	limitsWelcomeFields
		            				)
		            		 }
		              setFields={	acls
				        			?	updateLimitsAcls
				        			:	(	props.room
				        					?	updateRoomFields
				        					:	updateLimitsWelcomeFields
				        				)
		            		    }
		              projFields={props.room ? roomProjWelcomeFields : limitsWelcomeFields}
		              limits={props.limits}
		              room={props.room}
		              isLoaded={!loading}
		              acl={acls}
		              isReadOnly={seeAllOnly}
		              isProjectMember={isProjectMember}
		              greyedHint={	!acls &&
		              				fieldGreyedByCheckoutOff(field, checkoutOff)
		              				?	t(`welcome:fields:${field.field}:checkout_off`)
		              				:	undefined
		              		     }
		              field={field} />
	:	null
  )}
  </FlexRow>
  );
}

export interface WelcomeFieldProps /*extends WelcomeWizardProps*/ {
  type:					string;
  caption?:				string;
  fields:				UrlInfo[];
  setFields:			(fields: UrlInfo[]) => any;
  projFields:			UrlInfo[];
  field:				FieldDescriptor;
  limits?:				HotelLimits | null;
  room?:				HotelRoom;
  acl:					boolean;
  isLoaded?:			boolean;
  isReadOnly?:			boolean;
  isProjectMember?:		boolean;
  // Set: the field shows its value greyed and inert, with this text under it
  // (the Netflix box while the Checkout is switched off, #91 §3.2).
  greyedHint?:			string;
}

export const WelcomeField = (props: WelcomeFieldProps) => {
  const { t } = useTranslation();
  const role = React.useContext(RoleContext);
  const hotelChannelLists = useAppSelector((state) => state.hotelChannelLists);
  const loaded = props.isLoaded === undefined
  				 ?	true
  				 :	props.isLoaded;
  const greyed = !!props.greyedHint;
  const readOnly = props.isReadOnly || greyed;

  const set_name = props.field.features &&
    			   props.field.features.indexOf("set_name") > -1;

  const channellists_source =	props.field.features &&
    			   				props.field.features.indexOf("channellists_source") > -1;

  const hidden_user =	props.field.features &&
    			   		props.field.features.indexOf("hidden_user") > -1;

  const hide_default =	props.field.features &&
    			   		props.field.features.indexOf("hide_default") > -1;

  const use_textarea =	props.field.features &&
    			   		props.field.features.indexOf("textarea") > -1;

  // A checkbox that is ON when nothing is stored. Unticking writes an explicit
  // "0"; a room shows what applies to it - its own value, else the project's -
  // and re-ticking goes back to inheriting when the project is on. Never in
  // ACL mode: there it is an ordinary grant.
  //
  // No field uses this today (enable-room-ws was the only one, and the device
  // socket is no longer switchable). Kept because it is field-agnostic and the
  // next default-on setting would otherwise have to rebuild it.
  const default_on =	!props.acl &&
  						props.field.features &&
  						props.field.features.indexOf("default_on") > -1;

  const isScreentimeAcl =	props.acl &&
							props.field.field === screentimeAclKey;

  const legacyScreentimeOn =	isScreentimeAcl &&
								hasLegacyScreentimeAcl(props.fields);

  // Statistics has no legacy per-field keys of its own — while its ACL entry
  // is absent it instead inherits the Screen Time grant wholesale (§A4), so
  // the checkbox must read that as "on" the same way legacyScreentimeOn does
  // for screentimeAclKey, and unchecking it must write an explicit "0" (never
  // "") or the box would spring back on next render.
  const isStatisticsAcl =	props.acl &&
							props.field.field === statisticsAclKey;

  const statisticsFallbackOn =	isStatisticsAcl &&
									canOverrideScreentime(props.fields);

  const projValue = props.room
  					?	getField(
  					    	props.projFields
  					    	?	props.projFields
  					    	:	[],
  					    	props.field.field
  					    )
  					:	'';
  
  const lacls_pre =	props.limits &&
  					props.limits.acls
					?	props.limits.acls
					:	[];

  const disable =	!props.acl &&
			  		!default_on &&
			  		props.room &&
					!hide_default &&
					projValue != '' &&
					(role.admin || !props.field.acl || getField(lacls_pre, props.field.field) == "1"); /* &&
					!set_name;*/
  const defVal =	props.acl
					?	"1"
					:	(	disable
							?	"__disabled__"
							:	props.field.defval
						);
  /*const projValue = props.limits
  					?	getField(
  					    	props.limits.welcome.fields
  					    	?	props.limits.welcome.fields
  					    	:	[],
  					    	props.field.field
  					    )
  					:	'';
  const roomValue = props.room
  					?	getField(
  							props.room.fields
  							?	props.room.fields
  							:	[],
  							props.field.field
  						)
  					:	"";
  
  const value = props.room
  				?	roomValue
  				:	projValue;
  */
  
  const value = props.fields
  				?	getField(
						props.fields
						?	props.fields
						:	[],
						props.field.field
					)
				:	'';

  const updateValue = (val: string, fromTextInput?: boolean) => {
    let fields = copyUrls(props.fields);
    if (set_name) {
      switch(val) {
      case "":
        const currVal = getField(fields, props.field.field);
        fields = setField(fields, props.field.field + ".old", currVal);
        if (fromTextInput && hide_default)
          val = defVal;
        if (props.field.field === "custom-button") {
          const currList = getField(fields, "custom-channel-list");
          fields = setField(fields, "custom-channel-list.old", currList);
          fields = setField(fields, "custom-channel-list", "");
        }
        break;
      case defVal:
        if (props.acl)
          break;
        const oldVal = getField(fields, props.field.field + ".old");
        if (oldVal)
          val = oldVal;
        fields = setField(fields, props.field.field + ".old", "");
        if (props.field.field === "custom-button") {
          const oldList = getField(fields, "custom-channel-list.old");
          if (oldList)
            fields = setField(fields, "custom-channel-list", oldList);
          fields = setField(fields, "custom-channel-list.old", "");
        }
        break;
      }
      if (hide_default) {
        fields = fields.filter(f => !(f.type === props.field.field + ".old" && !f.url));
        if (props.field.field === "custom-button") {
          fields = fields.filter(f => !(f.type === "custom-channel-list.old" && !f.url));
        }
      }
    }
    fields = setField(fields, props.field.field, val);

    props.setFields(fields);
  };

  const expiresAt = props.fields
                    ?   getFieldExpiry(props.fields, props.field.field)
                    :   0;

  const updateExpiresAt = (ts: number) => {
    const fields = setFieldExpiry(props.fields || [], props.field.field, ts);
    props.setFields(fields);
  };

  const activatesAt = props.fields
                      ?   getFieldActivation(props.fields, props.field.field)
                      :   0;

  const updateActivatesAt = (ts: number) => {
    const fields = setFieldActivation(props.fields || [], props.field.field, ts);
    props.setFields(fields);
  };

  const isOffValue = (v: string) => v == "0" || v == "__disabled__";

  const defaultOnToggle =	value != ''
  							?	!isOffValue(value)
  							:	!(props.room && isOffValue(projValue));

  // Ticked: inherit when the project is on (a room), else an explicit "1".
  const defaultOnValue = (on: boolean): string =>
  	on
  	?	(props.room && !isOffValue(projValue) ? '' : "1")
  	:	"0";

  const hasActiveValue =	value !== '' &&
  							value !== '__disabled__';

  const isWelcomeFieldType =	props.type === 'welcome:fields';

  const showPicker = isWelcomeFieldType && !props.acl && !readOnly;

  // A default-on field has no schedule, but keeps the picker's slot: an
  // invisible icon of the same size, so its switch lines up with the others.
  const schedulePicker = showPicker && !default_on
                       ? <InlineSchedulePicker isActive={hasActiveValue}
                                               activatesAt={activatesAt}
                                               setActivatesAt={updateActivatesAt}
                                               expiresAt={expiresAt}
                                               setExpiresAt={updateExpiresAt}
                                               isReadOnly={readOnly} />
                       : showPicker
                       ? <span className="material-icons !text-[22px] invisible"
                               aria-hidden="true">alarm_on</span>
                       : null;

  const showSchedulePicker =	!channellists_source &&
  								props.field.field !== 'user-disability';


  /*const updateValue = (val: string) => {
    if (!props.limits)
      return;
    
    if (props.acl) {
      let limits = copyProjectLimits(props.limits);
      
      const acls =	limits.acls
					?	limits.acls
					:	[];
      
      limits.acls = setField(acls, props.field.field, val);
      props.setLimits(limits);
    } else
    if (props.room) {
      let room = copyProjectRoom(props.room);
      
      const fields =	room.fields
						?	room.fields
						:	[];
						
      room.fields = setField(fields, props.field.field, val);
      
      if (props.setRoom)
        props.setRoom(room);
    } else {
      let limits = copyProjectLimits(props.limits);
      
      const fields = 	limits.welcome.fields
						?	limits.welcome.fields
						:	[];
      
      limits.welcome.fields = setField(fields, props.field.field, val);
      props.setLimits(limits);
    }
  };*/
  
  const getComponent = (): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    
    const trBase = `${props.type}:${props.field.field}`
    let caption =	props.caption
    				?	props.caption
    				:	t(trBase + ":caption");

    const lacls =	props.limits &&
    				props.limits.acls
					?	props.limits.acls
					:	[];

    const toggle =	default_on
					?	defaultOnToggle
					:	isScreentimeAcl
					?	(	value == "1" ||
							(	value == "" &&
								legacyScreentimeOn
							)
						)
					:	isStatisticsAcl
					?	(	value == "1" ||
							(	value == "" &&
								statisticsFallbackOn
							)
						)
					:	(	(	props.acl ||
	    						props.field.type == 'checkbox'
	    					) &&
							(	(	!disable &&
									value != '' &&
									value != '__disabled__'
								) ||
								(	disable &&
									value == '__disabled__'
								)
							)
							?	true
							:	false
						);
    
    if (	(	props.acl &&
    			!props.field.acl
    		) ||
    		(	props.field.acl &&
				!role.admin &&
				!role?.privilages?.includes("SetManagerACLs") &&
				(!role?.privilages?.includes("SeeAllProjects") || props.isProjectMember) &&
				getField(lacls, props.field.field) != "1"
			)
       )
      return out;
    
    if (	props.field.type == 'checkbox' ||
        	props.acl
       ) {
      if (disable) {
        caption = t("welcome:disable-field", {field: caption});
      }
      out.push(
      <WelField key={0}
                label={caption}>
        <CheckBox toggled={toggle}
                  isLoaded={loaded}
                  isDisabled={greyed}
                  setToggled={readOnly ? () => {} : (val: boolean) =>
                    updateValue(default_on ? defaultOnValue(val) : val ? defVal : ((isScreentimeAcl || isStatisticsAcl) ? "0" : ''))
                  } />
        { schedulePicker }
      </WelField>
      );

      if (greyed)
        out.push(
        <div key="greyed"
             className="r12 text-[--Dark-Grey] max-w-[448px] pt-[4px]">
          { props.greyedHint }
        </div>
        );

      if (	props.acl ||
      		!props.field.features ||
      		!set_name ||
      		disable
      	 )
        return out;
        
      if (!use_textarea) {
        out.push(
        <WelField key={1}
                   className={[
                    'pt-[16px]',
                    //'transition-[height] duration-500',
                    'transition-[opacity] duration-500',
                    (value == '' || value == '__disabled__') ? 'max-h-0 opacity-0' : 'max-h-[80px] opacity-100',
                  ]}>
        {(role.admin || !hidden_user) &&
          <Input isLoaded={loaded}
                 label={t(trBase + ":set_name")}
                 isReadOnly={readOnly}
                 value={hide_default && value == defVal ? '' : value}
                 onValueChange={(v: string) => updateValue(v, true)} />
        }
        </WelField>
        );
      }

      if (use_textarea) {
        out.push(
        <FlexRow key={2}
                 direction="column"
                 align="start"
                 className={[
                  'pt-[16px]',
                  'max-w-[448px]',
                  'transition-[opacity] duration-500',
                  'overflow-hidden',
                  (value == '' || value == '__disabled__') ? 'max-h-0 opacity-0' : 'max-h-[200px] opacity-100',
                ]}>
        {(role.admin || !hidden_user) &&
          <TextArea isLoaded={loaded}
                    label={t(trBase + ":set_name")}
                    isReadOnly={readOnly}
                    value={hide_default && value == defVal ? '' : value}
                    onValueChange={(v: string) => updateValue(v, true)}
                    showChars={false} />
        }
        </FlexRow>
        );
      }
    } else
    if (props.field.type == 'input') {
      out.push(
      <WelField key={0}>
        <Input key={1}
               isLoaded={loaded}
               isReadOnly={readOnly}
               label={caption}
               value={value}
               onValueChange={updateValue} />
        { schedulePicker }
      </WelField>
      );
    } else
    if (props.field.type == 'text') {
      out.push(
      <WelField key={0}
                className="max-w-[996px]">
        <TextArea value={value}
                  isLoaded={loaded}
                  isReadOnly={readOnly}
                  onValueChange={updateValue}
                  label={caption}
                  placeholder={props.field.desc}
                  showChars={false} />
        { schedulePicker }
      </WelField>
      );
    } else
    if (props.field.type == 'dropdown') {
      let welOpts: FieldInfo[] =
        props.field.options
      	?	props.field.options.map((opt) => ({
      		  value:		opt.value,
      		  desc:			opt.desc,
      		}))
      	:	[];

      if (props.room && props.field.field === 'user-disability') {
        welOpts = welOpts.map((opt) => opt.value === '' ? { ...opt, value: 'off' } : opt);
        welOpts.unshift({ value: '', desc: t('welcome:fields:project_default') });
      }

      if (channellists_source) {
        welOpts = (hotelChannelLists.lists || [])
          .filter((l: ChannelList) => l.hidden && !l.to_delete)
          .map((l: ChannelList) => ({ value: l.epg_id, desc: l.name }));
      }

      out.push(
      <WelField key={0}>
        <Dropdown key={1}
                  label={caption}
              	  value={value}
                  isDisabled={readOnly}
                  options={welOpts}
                  setValue={(val) => updateValue(val.toString())}>

          <>
	      </>
        </Dropdown>
        { showSchedulePicker && schedulePicker }
      </WelField>
      );
    }
    
    return out;
  };

   /* <CheckBox toggled={value != ''}
                setToggled={(val: boolean) =>
                            updateValue(val ? props.field.defval : '')
                         } />
    {props.field.features &&
     props.field.features.indexOf("set_name") > -1 &&
      <Input label={t('projects:project:project_settings:id')}
             isInvalid={props.idInvalid}
             errorMessage={t('projects:project:project_settings:id_invalid')}
             value={props.project.id}
             onValueChange={updateId}/> 
    }
  */
  
  if (!props.acl && (props.field.field === "enable-apps" || props.field.field === "enable-channellist" || props.field.field === "enable-theme" || props.field.field === "enable-language" || props.field.field === "enable-public-ip" || (props.field.field === "enable-welcome" && !props.room)))
    return null;

  if (props.field.field === "custom-channel-list") {
    if (props.acl)
      return null;
    const toggleVal = getField(props.fields, "custom-button");
    if (!toggleVal || toggleVal === "__disabled__")
      return null;
  }

  const skipVisibility = props.field.field === "enable-welcome";

  if (	!props.acl &&
        !skipVisibility &&
	  	(	(	props.room &&
	  			props.field.visibility.indexOf("room") < 0
	  		) ||
	  		(	!props.room &&
	  			props.field.visibility.indexOf("hotel") < 0
	  		)
	  	)
     )
    return null;
  
  const items = getComponent();
  if (	!items ||
     	items.length == 0
     )
    return null;
  
  return (
  <FlexRow className="py-[8px]"
           align="start"
           direction="column">
  {items.map((item) =>
    item
  )}
  </FlexRow>
  );
};

export default WelcomeWizard;




